import { gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  Flex,
  ModalBody,
  Spacer,
  useToast,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { EditorState, convertToRaw } from 'draft-js';
import * as React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { useUpdateCacheOnPostCreation } from '@/components/PostingExperience/useUpdateCacheOnPostCreation';
import { SchedulePostMenu } from '@/components/post-form/SchedulePostMenu';
import { SimplePostForm } from '@/components/post-form/SimplePostForm';
import { usePermissions } from '@/hooks/usePermissions';
import { logError } from '@/imports/logging/ClientLogger';
import { useAnalyticsLogger } from '@/imports/ui/amplitude/helpers';
import { AnnouncePostModal } from '@/imports/ui/components/feed-item/AnnouncePostModal';

import {
  CreatePostModal_CreateSimplePost,
  CreatePostModal_CreateSimplePostVariables,
} from './graphql-types/CreatePostModal_CreateSimplePost';

import { PostingModalFooter } from '../PostingModalFooter';

const CREATE_POST = gql`
  mutation CreatePostModal_CreateSimplePost($input: CreateSimplePostInput!) {
    createSimplePost(input: $input) {
      data {
        ...UseUpdateCacheOnPostCreation_PostV2Fragment
        author {
          _id
          userOnboardingChecklistState {
            userHasPosted
          }
        }
        group {
          _id
          isGlobalSingleton
        }
      }
    }
  }
  ${useUpdateCacheOnPostCreation.postFragment}
`;

type ICreatePostModalProps = {
  onClose: () => void;
  createArticle: () => void;
  group?: { _id: string };
  organization?: { _id: string };
  defaultIsSponsoredPost?: boolean;
  tags?: { _id: string; name: string }[];
};

type SimplePostFormValues = {
  title: string;
  group: string;
  images: ({ imageId: string } | { videoId: string })[];
  body: EditorState;
  isSponsoredPost: boolean;
  isAnnouncement: boolean;
  tags?: { _id: string; name: string }[];
  attachments: { versionFileId: string; fileName: string }[];
  scheduledPublishDate: Date | null;
};

export const CreatePostModal: React.FC<ICreatePostModalProps> = (props) => {
  const [isAnnounceModalOpen, setIsAnnounceModalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const permissions = usePermissions();
  const canSchedulePosts = permissions.canDoAdminStuffAndAuthenticated;

  const toast = useToast();

  const methods = useForm<SimplePostFormValues>({
    defaultValues: {
      title: '',
      group: props.group?._id,
      images: [],
      body: EditorState.createEmpty(),
      isSponsoredPost: !!props.defaultIsSponsoredPost,
      isAnnouncement: false,
      tags: props.tags || [],
      attachments: [],
      scheduledPublishDate: null,
    },
  });
  const { handleSubmit, formState, watch } = methods;

  const isAnnouncement = watch('isAnnouncement');
  const scheduledPublishDate = watch('scheduledPublishDate');

  const [createPost] = useMutation<
    CreatePostModal_CreateSimplePost,
    CreatePostModal_CreateSimplePostVariables
  >(CREATE_POST, { refetchQueries: ['AccountMenu_Query'] });

  const analyticsClient = useAnalyticsLogger();

  const { updateCacheOnPostCreation } = useUpdateCacheOnPostCreation();

  const onSubmit = async (params: SimplePostFormValues) => {
    setLoading(true);

    try {
      const result = await createPost({
        variables: {
          input: {
            post: {
              title: params.title,
              body: convertToRaw(params.body.getCurrentContent()),
              versionFileIds: params.attachments.map((attachment) => {
                return attachment.versionFileId;
              }),
              tagIds: params.tags?.map((tag) => tag._id),
              embeddedImageIds: params.images
                .filter((id): id is { imageId: string } => 'imageId' in id)
                .map((id) => id.imageId),
              ...(props.organization
                ? params.isSponsoredPost
                  ? {
                      sponsorData: {
                        isSponsoredPost: true,
                        audience: { groupId: params.group },
                      },
                      organizationId: props.organization._id,
                    }
                  : { organizationId: props.organization._id }
                : { groupId: params.group }),
              scheduledPublishDate: params.scheduledPublishDate?.toISOString(),
            },
            isAnnouncement: params.isAnnouncement,
          },
        },
        update(cache, { data }) {
          if (!data?.createSimplePost.data) {
            return;
          }
          updateCacheOnPostCreation({
            cache,
            post: data.createSimplePost.data,
          });
        },
      });
      analyticsClient.logEvent_comm_click_post({
        'added tags': !!params.tags && params.tags.length > 0,
        announcement: params.isAnnouncement,
        'posted in': props.organization
          ? 'organization'
          : result.data?.createSimplePost.data.group?.isGlobalSingleton
          ? 'general'
          : 'group',
        'is hosted by sponsor': params.isSponsoredPost,
      });
      props.onClose();

      !!params.scheduledPublishDate &&
        toast({
          description: `Post scheduled for ${dayjs(
            params.scheduledPublishDate
          ).format('MMM D, h:mm A')}`,
          status: 'success',
        });
    } catch (err) {
      toast({
        status: 'error',
        description: 'Failed to create post.',
      });
      logError(err);
    } finally {
      setLoading(false);
    }
  };

  const onInvalid = () => {
    /**
     * If the form is invalid, reset `scheduledPublishDate`
     * User should be able to click `Post` to create the post
     * immediately after the form fields become valid
     */
    methods.resetField('scheduledPublishDate');
  };

  return (
    <FormProvider {...methods}>
      <AnnouncePostModal
        open={isAnnounceModalOpen}
        onClose={() => setIsAnnounceModalOpen(false)}
        onClickAnnounce={() => {
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        onClickPost={() => {
          methods.setValue('isAnnouncement', false);
          handleSubmit(onSubmit, onInvalid)();
          setIsAnnounceModalOpen(false);
        }}
        announcementLoading={false}
        loading={false}
      />

      <ModalBody px="6" py="4">
        <SimplePostForm
          organizationId={props.organization?._id}
          initialGroupId={props.group?._id}
        />
      </ModalBody>

      <PostingModalFooter>
        <Flex w="100%" align="center">
          <Button
            variant="ghost"
            colorScheme="gray"
            onClick={props.onClose}
            display={{ base: 'inline-flex', md: 'inline-flex', lg: 'none' }}
            mr={2}
            border="1px solid"
            borderColor="gray.300"
          >
            Cancel
          </Button>
          <Spacer />
          {canSchedulePosts && (
            <Controller
              name="scheduledPublishDate"
              render={({ field: { onBlur, onChange, ref, value, name } }) => (
                <Box mr={2}>
                  <SchedulePostMenu
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={
                      (formState.isSubmitted && !formState.isValid) || loading
                    }
                    validate={() => {
                      if (!value) {
                        methods.setError(name, {
                          message: 'A date and time must be specified',
                        });
                        return false;
                      } else {
                        methods.clearErrors(name);
                        return true;
                      }
                    }}
                    validationMessage={
                      formState.errors.scheduledPublishDate?.message
                    }
                  />
                </Box>
              )}
            />
          )}
          <Button
            isLoading={loading}
            isDisabled={formState.isSubmitted && !formState.isValid}
            onClick={
              isAnnouncement
                ? () => setIsAnnounceModalOpen(true)
                : handleSubmit(onSubmit, onInvalid)
            }
          >
            {scheduledPublishDate ? 'Schedule Post' : 'Post Now'}
          </Button>
        </Flex>
      </PostingModalFooter>
    </FormProvider>
  );
};
